import { Alert, Collapse, Empty, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { AdvisoryAiReportRawData } from '@/features/advisory-ai/types'
import { revaluationMissingMessage } from '@/features/advisory-ai/components/revaluationMessages'
import {
  REPORT_SUBSECTIONS,
  columnTitle,
  compareMonthValues,
  findMonthKey,
  formatMonthValue,
  isPercentColumn,
  lookupSubsection,
  sortProfitabilityRows,
} from './sourceReportsConfig'

const { Text } = Typography

// ── number formatting ──
function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
  return null
}

const withCommas = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Currency: $ prefix, negatives in parentheses — e.g. -1234.5 -> "($1,234.50)".
function formatCurrency(n: number): string {
  const body = `$${withCommas(Math.abs(n))}`
  return n < 0 ? `(${body})` : body
}

// Percentage: 2 decimals + % suffix (keeps the minus sign) — e.g. -8.2828… -> "-8.28%".
function formatPercent(n: number): string {
  return `${withCommas(n)}%`
}

function formatCellValue(value: unknown, percent: boolean): string {
  if (value === null || value === undefined || value === '') return '—'
  const num = toNumber(value)
  if (percent) return num !== null ? formatPercent(num) : String(value)
  if (num !== null) return formatCurrency(num)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

function isNumericLike(value: unknown): boolean {
  return toNumber(value) !== null
}

function buildColumns(
  rows: Array<Record<string, unknown>>,
  endDate?: string | null
): ColumnsType<Record<string, unknown>> {
  const keys = new Set<string>()
  rows.forEach((row) => Object.keys(row).forEach((k) => keys.add(k)))
  return Array.from(keys).map((key) => {
    const allNumericOrNull = rows.every((row) => row[key] == null || isNumericLike(row[key]))
    const percent = isPercentColumn(key)
    return {
      title: columnTitle(key, endDate),
      dataIndex: key,
      key,
      align: allNumericOrNull ? ('right' as const) : ('left' as const),
      render: (value: unknown) => formatCellValue(value, percent),
    }
  })
}

// A single-row subsection (e.g. an AP/AR aging summary) is one wide, sideways-scrolling row.
// Transpose it: each column becomes a "Field → Value" row, read top-to-bottom.
function TransposedRow({
  row,
  endDate,
}: {
  row: Record<string, unknown>
  endDate?: string | null
}) {
  const entries = Object.keys(row).map((key) => ({
    key,
    field: columnTitle(key, endDate),
    value: formatCellValue(row[key], isPercentColumn(key)),
    numeric: isNumericLike(row[key]),
  }))
  const columns: ColumnsType<(typeof entries)[number]> = [
    { title: 'Field', dataIndex: 'field', key: 'field' },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (value: string, record) => (
        <span className={record.numeric ? 'font-medium tabular-nums' : undefined}>{value}</span>
      ),
    },
  ]
  return (
    <Table
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={entries}
      pagination={false}
      showHeader={false}
    />
  )
}

// "Monthly" source reports come back long (one row per dimension per month, with a Month column).
// When the rows carry a dimension column, pivot the months into columns so each dimension is a
// single row read left-to-right across months — far tidier than the repeated long form.
// One value column → a column per month; several → a column group per month.
function MonthlyPivotTable({
  rows,
  monthKey,
  dimCols,
  valueCols,
}: {
  rows: Array<Record<string, unknown>>
  monthKey: string
  dimCols: string[]
  valueCols: string[]
}) {
  const months = Array.from(
    new Set(rows.map((r) => r[monthKey]).filter((v): v is string => typeof v === 'string' && v !== ''))
  ).sort(compareMonthValues)

  const dimKey = (row: Record<string, unknown>) => JSON.stringify(dimCols.map((c) => row[c] ?? null))

  const tuples: Array<{ k: string; dims: unknown[] }> = []
  const tupleSeen = new Set<string>()
  const byTupleMonth = new Map<string, Record<string, unknown>>()
  rows.forEach((row) => {
    const k = dimKey(row)
    if (!tupleSeen.has(k)) {
      tupleSeen.add(k)
      tuples.push({ k, dims: dimCols.map((c) => row[c] ?? null) })
    }
    const mv = row[monthKey]
    if (typeof mv === 'string' && mv !== '') byTupleMonth.set(`${k}__${mv}`, row)
  })

  const soleValueCol = valueCols.length === 1 ? valueCols[0] : undefined

  const columns: ColumnsType<Record<string, unknown>> = dimCols.map((c) => ({
    title: columnTitle(c),
    dataIndex: c,
    key: c,
    align: 'left',
    render: (value: unknown) => (value == null || value === '' ? '—' : String(value)),
  }))
  months.forEach((mv, mi) => {
    const label = formatMonthValue(mv)
    if (soleValueCol) {
      const percent = isPercentColumn(soleValueCol)
      columns.push({
        title: label,
        dataIndex: `m${mi}`,
        key: `m${mi}`,
        align: 'right',
        render: (value: unknown) => formatCellValue(value, percent),
      })
    } else {
      columns.push({
        title: label,
        key: `m${mi}`,
        children: valueCols.map((vc) => {
          const percent = isPercentColumn(vc)
          return {
            title: columnTitle(vc),
            dataIndex: `m${mi}__${vc}`,
            key: `m${mi}__${vc}`,
            align: 'right' as const,
            render: (value: unknown) => formatCellValue(value, percent),
          }
        }),
      })
    }
  })

  const dataSource = tuples.map((t, ri) => {
    const row: Record<string, unknown> = { _rk: ri }
    dimCols.forEach((c, ci) => {
      row[c] = t.dims[ci]
    })
    months.forEach((mv, mi) => {
      const src = byTupleMonth.get(`${t.k}__${mv}`)
      if (soleValueCol) {
        row[`m${mi}`] = src ? src[soleValueCol] ?? null : null
      } else {
        valueCols.forEach((vc) => {
          row[`m${mi}__${vc}`] = src ? src[vc] ?? null : null
        })
      }
    })
    return row
  })

  return (
    <Table
      size="small"
      rowKey="_rk"
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      scroll={{ x: 'max-content' }}
      bordered
    />
  )
}

export function SubsectionTable({
  rows: inputRows,
  endDate,
  orderByProfitability = false,
}: {
  rows: Array<Record<string, unknown>>
  endDate?: string | null
  // Reorder rows by the canonical P&L Group → Account Type sequence (Profitability reports).
  orderByProfitability?: boolean
}) {
  if (inputRows.length === 0) return <Text type="secondary" className="block text-sm">No rows.</Text>

  // Sorting up front propagates to both the monthly-pivot and plain-table paths below,
  // and (via first-seen tuple discovery) orders the pivot's dimension rows too.
  const rows = orderByProfitability ? sortProfitabilityRows(inputRows) : inputRows

  // Monthly tables with a dimension column read better pivoted (months → columns).
  const monthKey = findMonthKey(rows)
  if (monthKey) {
    const colOrder: string[] = []
    const seen = new Set<string>()
    rows.forEach((row) =>
      Object.keys(row).forEach((k) => {
        if (!seen.has(k)) {
          seen.add(k)
          colOrder.push(k)
        }
      })
    )
    const isValueCol = (c: string) => rows.every((r) => r[c] == null || isNumericLike(r[c]))
    const valueCols = colOrder.filter((c) => c !== monthKey && isValueCol(c))
    const dimCols = colOrder.filter((c) => c !== monthKey && !isValueCol(c))
    if (dimCols.length > 0 && valueCols.length > 0) {
      return <MonthlyPivotTable rows={rows} monthKey={monthKey} dimCols={dimCols} valueCols={valueCols} />
    }
  }

  if (rows.length === 1 && rows[0]) return <TransposedRow row={rows[0]} endDate={endDate} />
  return (
    <Table
      size="small"
      rowKey={(_, index) => String(index ?? 0)}
      columns={buildColumns(rows, endDate)}
      dataSource={rows}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  )
}

// ── per-organisation pivot (consolidated reports carry a "perOrg" section) ──
type PerOrgReports = Record<string, Array<Record<string, unknown>>>

// Reads the consolidated report's "perOrg" map: { orgId: { name, reports: { subsection: rows } } }.
function getPerOrg(
  reportJson?: AdvisoryAiReportRawData | null
): Array<{ name: string; reports: PerOrgReports }> | null {
  const raw = (reportJson as Record<string, unknown> | null | undefined)?.['perOrg']
  if (!raw || typeof raw !== 'object') return null
  const entries = Object.entries(raw as Record<string, unknown>).map(([orgId, value]) => {
    const entry = (value ?? {}) as { name?: string; reports?: PerOrgReports }
    return { name: entry.name || orgId, reports: entry.reports || {} }
  })
  return entries.length > 0 ? entries : null
}

// Pivots a subsection's per-org rows so the shared dimension columns (e.g. Month, P&L Group)
// stay once and each org gets its own value column(s). One value column → a column per org;
// many value columns → a column group per org.
function PerOrgSubsectionTable({
  orgs: inputOrgs,
  subsection,
  endDate,
  orderByProfitability = false,
}: {
  orgs: Array<{ name: string; rows: Array<Record<string, unknown>> | null }>
  subsection: string
  endDate?: string | null
  // Reorder each org's rows by the canonical P&L Group → Account Type sequence (Profitability reports).
  orderByProfitability?: boolean
}) {
  // Sorting each org's rows makes both the shared-tuple discovery order and the
  // org-grouped (multi-value) layout follow the canonical accounting sequence.
  const orgs = orderByProfitability
    ? inputOrgs.map((o) => ({ ...o, rows: o.rows ? sortProfitabilityRows(o.rows) : o.rows }))
    : inputOrgs
  const allRows = orgs.flatMap((o) => o.rows ?? [])
  if (allRows.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${subsection} data available.`} />
  }

  const colOrder: string[] = []
  const seen = new Set<string>()
  allRows.forEach((row) =>
    Object.keys(row).forEach((k) => {
      if (!seen.has(k)) {
        seen.add(k)
        colOrder.push(k)
      }
    })
  )
  const isValueCol = (c: string) => allRows.every((r) => r[c] == null || isNumericLike(r[c]))
  const keyCols = colOrder.filter((c) => !isValueCol(c))
  const valueCols = colOrder.filter(isValueCol)
  const soleValueCol = valueCols.length === 1 ? valueCols[0] : undefined

  const textRender = (value: unknown) => (value == null || value === '' ? '—' : String(value))

  // No shared dimension column and a single row per org (e.g. an AP/AR aging summary):
  // transpose so each value field is a row and each organisation is a column. Aging
  // summaries have few buckets, so this stays short and avoids sideways scrolling.
  const singleRowPerOrg = orgs.every((o) => (o.rows?.length ?? 0) <= 1)
  if (keyCols.length === 0 && valueCols.length > 0 && singleRowPerOrg) {
    const columns: ColumnsType<Record<string, unknown>> = [
      { title: '', dataIndex: '_field', key: '_field', render: (value: unknown) => String(value) },
      ...orgs.map((o, oi) => ({
        title: o.name,
        dataIndex: `org${oi}`,
        key: `org${oi}`,
        align: 'right' as const,
        render: (value: unknown, record: Record<string, unknown>) =>
          formatCellValue(value, Boolean(record['_percent'])),
      })),
    ]
    const dataSource = valueCols.map((vc, ri) => {
      const row: Record<string, unknown> = { _rk: ri, _field: columnTitle(vc, endDate), _percent: isPercentColumn(vc) }
      orgs.forEach((o, oi) => {
        row[`org${oi}`] = o.rows?.[0]?.[vc] ?? null
      })
      return row
    })
    return (
      <Table
        size="small"
        rowKey="_rk"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
      />
    )
  }

  // One value column → orgs become columns, e.g. "Month | Consolidated | Acme | Beta".
  if (soleValueCol) {
    const vc = soleValueCol
    const keyOf = (row: Record<string, unknown>) => JSON.stringify(keyCols.map((c) => row[c] ?? null))
    const tuples: Array<{ k: string; vals: unknown[] }> = []
    const tupleSeen = new Set<string>()
    allRows.forEach((row) => {
      const k = keyOf(row)
      if (!tupleSeen.has(k)) {
        tupleSeen.add(k)
        tuples.push({ k, vals: keyCols.map((c) => row[c] ?? null) })
      }
    })
    const orgIndex = orgs.map((o) => {
      const map = new Map<string, Record<string, unknown>>()
      ;(o.rows ?? []).forEach((row) => map.set(keyOf(row), row))
      return map
    })
    const columns: ColumnsType<Record<string, unknown>> = keyCols.map((c) => ({
      title: columnTitle(c, endDate),
      dataIndex: c,
      key: c,
      align: 'left' as const,
      render: textRender,
    }))
    orgs.forEach((o, oi) => {
      columns.push({
        title: o.name,
        dataIndex: `org${oi}`,
        key: `org${oi}`,
        align: 'right',
        render: (value: unknown) => formatCellValue(value, isPercentColumn(vc)),
      })
    })
    const dataSource = tuples.map((t, ri) => {
      const row: Record<string, unknown> = { _rk: ri }
      keyCols.forEach((c, ci) => {
        row[c] = t.vals[ci]
      })
      orgs.forEach((_, oi) => {
        const orgRow = orgIndex[oi]?.get(t.k)
        row[`org${oi}`] = orgRow ? orgRow[vc] ?? null : null
      })
      return row
    })
    return (
      <Table
        size="small"
        rowKey="_rk"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
      />
    )
  }

  // Multiple value columns → organisation becomes a leading row dimension so the value columns
  // appear once (no per-org repetition). Consecutive rows of the same org share one merged cell.
  const columns: ColumnsType<Record<string, unknown>> = [
    {
      title: 'Organisation',
      dataIndex: '_org',
      key: '_org',
      onCell: (record) => ({ rowSpan: Number((record as Record<string, unknown>)['_orgSpan'] ?? 1) }),
      render: (value: unknown) => String(value),
    },
    ...keyCols.map((c) => ({
      title: columnTitle(c, endDate),
      dataIndex: c,
      key: c,
      align: 'left' as const,
      render: textRender,
    })),
    ...valueCols.map((vc) => ({
      title: columnTitle(vc, endDate),
      dataIndex: vc,
      key: vc,
      align: 'right' as const,
      render: (value: unknown) => formatCellValue(value, isPercentColumn(vc)),
    })),
  ]
  const dataSource: Array<Record<string, unknown>> = []
  let rowKeyCounter = 0
  orgs.forEach((o) => {
    const rows = o.rows ?? []
    rows.forEach((r, idx) => {
      dataSource.push({ ...r, _org: o.name, _orgSpan: idx === 0 ? rows.length : 0, _rk: rowKeyCounter })
      rowKeyCounter += 1
    })
  })

  return (
    <Table
      size="small"
      rowKey="_rk"
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      scroll={{ x: 'max-content' }}
      bordered
    />
  )
}

// ── Source Reports: Report ▸ Subsection (per-org columns when the report is consolidated) ──
export function SourceReportsSection({
  reportJson,
  endDate,
  revaluationMissing = false,
}: {
  reportJson?: AdvisoryAiReportRawData | null
  endDate?: string | null
  // Backend flag: the source pull came back without bank revaluation data.
  revaluationMissing?: boolean
}) {
  const perOrg = getPerOrg(reportJson)

  const reportTypeItems = REPORT_SUBSECTIONS.map((reportType) => {
    // Profitability items follow the canonical P&L Group → Account Type order, not the query's order.
    const orderByProfitability = reportType.label === 'Profitability'
    return {
      key: reportType.label,
      label: reportType.label,
      children: (
        <Collapse
          items={reportType.subsections
            // "… Per Org" subsections only make sense for consolidated analyses.
            .filter((sub) => !sub.perOrg || perOrg)
            .map((sub) => {
              if (sub.perOrg && perOrg) {
                // One column per company (the combined figure lives in the matching non-perOrg subsection).
                const orgs = perOrg.map((o) => ({ name: o.name, rows: lookupSubsection(o.reports, sub.dataKey) }))
                return {
                  key: sub.label,
                  label: sub.label,
                  children: (
                    <PerOrgSubsectionTable
                      orgs={orgs}
                      subsection={sub.label}
                      endDate={endDate}
                      orderByProfitability={orderByProfitability}
                    />
                  ),
                }
              }
              // Consolidated/own combined figures as a single table.
              const rows = lookupSubsection(reportJson, sub.dataKey)
              return {
                key: sub.label,
                label: sub.label,
                children:
                  rows === null ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${sub.label} data available.`} />
                  ) : (
                    <SubsectionTable rows={rows} endDate={endDate} orderByProfitability={orderByProfitability} />
                  ),
              }
            })}
        />
      ),
    }
  })

  return (
    <div>
      <p className="text-sm text-gray-600 mb-3">
        Source data extracted from the underlying reports and used to generate the AI analysis.
        {perOrg
          ? ' “… Per Org” subsections show a column per company; the others show the consolidated figures.'
          : ' Expand a report, then a subsection, to view its table.'}
      </p>
      {revaluationMissing ? (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={revaluationMissingMessage(!!perOrg)}
        />
      ) : null}
      <Collapse items={reportTypeItems} defaultActiveKey={REPORT_SUBSECTIONS[0] ? [REPORT_SUBSECTIONS[0].label] : []} />
    </div>
  )
}
